import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { PDFExport, savePDF } from '@progress/kendo-react-pdf';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row, Card, CardBody, Modal } from 'reactstrap';
import Notification from 'rc-notification';
import SmallLoader from '../../../shared/components/SmallLoader';
import FilterHeader from '../../../shared/components/FilterHeader';
import { RTLProps } from '../../../shared/prop-types/ReducerProps';
import ResizableDataTable from '../../Tables/ResizableTable/components/ResizableReactTable';
import MonthlySales from './components/MonthlySales';
import HorizontalBars from '../../Charts/ChartJs/components/HorizontalBar';
import YearBars from '../../Charts/ChartJs/components/YearBarChar';
import BTC from '../Finance/components/BTC';
import { getRevenueData, handleLoader } from '../../../redux/actions/revenueAction';
import { setCurrencyFormat } from '../../../utils/helpers';
import { vendorNameMapping, sortType } from '../helper';

const reactTableData = {
  tableHeaderData: [
    {
      Header: 'Sales Year',
      accessor: 'salesYear',
      disableGlobalFilter: false,
      sortType
    },
    {
      Header: 'Sales Month',
      accessor: 'salesMonthStr',
      disableGlobalFilter: false,
      sortType
    },
    {
      Header: 'Order',
      accessor: 'orderCount',
      disableGlobalFilter: false,
      rightAlign: true,
      sortType

    },
    {
      Header: 'Amount',
      accessor: 'amount',
      disableGlobalFilter: false,
      rightAlign: true,
      sortType
    },
    {
      Header: 'Sales Channel',
      accessor: 'salesChannel',
      disableGlobalFilter: false,
      sortType
    },
  ],
};

const Revenue = ({ dispatch, rtl, vendor }) => {
  const { t } = useTranslation('common');
  const location = useLocation();

  const propsData = useSelector(
    state =>
    ({
      revenueDataComplete: state.revenueReducer && state.revenueReducer?.[vendor]?.revenueData,
      isLoading: state.revenueReducer && state.revenueReducer?.[vendor]?.isLoading,
      salesChannelFilterData: state.marketPlaceReducer && state.marketPlaceReducer.salesChannel
    })
  )
  const { revenueDataComplete, salesChannelFilterData, isLoading } = propsData;
  const [isModel, setModel] = useState(false);
  const [revenueData, setData] = useState(revenueDataComplete?.modal?.data ?? []);

  useEffect(() => {
    const OptChennel = 'salesChannel';
    if (salesChannelFilterData && salesChannelFilterData.length > 0) {
      if (
        revenueDataComplete?.modal?.data?.length > 0
      ) {
        setData(
          revenueDataComplete.modal.data.filter(
            x =>
              salesChannelFilterData?.length > 0 &&
              salesChannelFilterData?.find(y => (y?.value?.toString() || '').toLowerCase()
                === x[OptChennel]?.toLowerCase()),
          ),
        );
      }
    } else {
      setData(revenueDataComplete?.modal?.data ?? []);
    }
  }, [revenueDataComplete, salesChannelFilterData])

  useEffect(() => {
    if (vendor && (!revenueDataComplete || !revenueDataComplete[vendor])) {
      console.log('Fetching revenue data for vendor:', vendor);
      dispatch(getRevenueData({ vendor })); // Fix: Pass object with vendor property
    }
  }, [vendor, dispatch, revenueDataComplete]);

  const changeFilter = (val, Opt) => {
    if (val && val.length > 0) {
      if (
        revenueDataComplete?.modal?.data?.length > 0 &&
        Opt !== 'Quarter'
      ) {
        setData(
          revenueDataComplete.modal.data.filter(
            x =>
              val.find(y => (y?.value?.toString() || '').toLowerCase() === x[Opt]?.toLowerCase()),
          ),
        );
      }
    } else {
      setData(revenueDataComplete?.modal?.data ?? []);
    }
  };

  const container = React.useRef(null);
  const exportToPDF = () => {
    // console.log('Hello')
    Notification.newInstance({}, (notification) => {
      notification.notice({
        content: 'PDF loading......',
        duration: 5,
        style: { top: 120, fontWeight: "bold" },
        maxCount: 10
      });
    });
    const dvMonthlyVR = document.getElementById('btnMonthlyViewReport');
    const dvYearlyVR = document.getElementById('btnYearlyViewReport');
    const dvHorVR = document.getElementById('btnHorViewReport');
    const dvBTCVR = document.getElementById('btnBTCViewReport');
    dvMonthlyVR.style.display = "none";
    dvYearlyVR.style.display = "none";
    dvHorVR.style.display = "none";
    dvBTCVR.style.display = "none";
    const element = container.current || document.body;

    savePDF(element, {
      paperSize: "auto",
      Header: "test",
      margin: { left: "1cm", top: "1cm", right: "1cm", bottom: "1cm" },
      // margin: "2cm",
      // forcePageBreak: ".page-break",
      //   landscape: true,
      //  multiPage: true,
      fileName: `${vendorNameMapping[vendor]} Sales Revenue Report ${new Date().getFullYear()}`,
    })
    dvMonthlyVR.style.display = "block";
    dvYearlyVR.style.display = "block";
    dvHorVR.style.display = "block";
    dvBTCVR.style.display = "block";
  }

  const addMonthlyAmount = (total, num) => Number(total) + Number(num);

  const reduceArray = (obj) => {
    if (obj && obj.length > 0) {
      return obj.reduce(addMonthlyAmount);
    }
    return 0;
  };

  useEffect(() => {
    dispatch(handleLoader(true, vendor));
    dispatch(getRevenueData({ vendor }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setData(revenueDataComplete?.modal?.data ?? []);
  }, [revenueDataComplete]);

  const yearArray = [];
  if (revenueData?.length > 0) {
    revenueData.forEach((innerObj) => {
      if (!yearArray.includes(innerObj?.salesYear)) {
        yearArray.push(innerObj.salesYear);
      }
    });
  }
  const openModel = () => {
    setModel(true);
  };

  const renderBTCData = () => {
    if (yearArray && yearArray.length > 0) {
      let maxBTC = 0;
      revenueData?.forEach((item) => {
        if (maxBTC < Number(item.amount))
          maxBTC = Number(item.amount)
      })
      return yearArray.map((year) => {
        const yearData = revenueData?.filter(x => x.salesYear === year);

        const monthWiseData = yearData?.reduce((acc, current) => {
          if (acc[current?.salesMonth]) {
            acc[current?.salesMonth].push(current)
          } else {
            acc[current?.salesMonth] = [current];
          }
          return acc
        }, {});

        const jan = monthWiseData[1]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2)
        const feb = monthWiseData[2]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2)
        const mar = monthWiseData[3]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2)
        const apr = monthWiseData[4]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2)
        const may = monthWiseData[5]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2)
        const jun = monthWiseData[6]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2)
        const jul = monthWiseData[7]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2)
        const aug = monthWiseData[8]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2)
        const sep = monthWiseData[9]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2)
        const oct = monthWiseData[10]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2)
        const nov = monthWiseData[11]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2)
        const dec = monthWiseData[12]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2)

        const q1 = [
          { name: 'Jan', btc: jan },
          { name: 'Feb', btc: feb },
          { name: 'Mar', btc: mar },
        ];
        const q2 = [
          { name: 'Apr', btc: apr },
          { name: 'May', btc: may },
          { name: 'Jun', btc: jun },
        ];
        const q3 = [
          { name: 'Jul', btc: jul },
          { name: 'Aug', btc: aug },
          { name: 'Sep', btc: sep },
        ];
        const q4 = [
          { name: 'Oct', btc: oct },
          { name: 'Nov', btc: nov },
          { name: 'Dec', btc: dec },
        ];
        return (
          <tr className="graph_table_row" key={year}>
            <td className="graph_table_row_td">{year}</td>
            <td className="graph_table_row_td">
              <BTC dataSet={q1} dir={rtl.direction} maxBTCValue={maxBTC} />
            </td>
            <td className="graph_table_row_td">
              <BTC dataSet={q2} dir={rtl.direction} maxBTCValue={maxBTC} />
            </td>
            <td className="graph_table_row_td">
              <BTC dataSet={q3} dir={rtl.direction} maxBTCValue={maxBTC} />
            </td>
            <td className="graph_table_row_td">
              <BTC dataSet={q4} dir={rtl.direction} maxBTCValue={maxBTC} />
            </td>
          </tr>
        );

      })
    }

    return null;
  }

  // eslint-disable-next-line max-len
  const revenueDataFormated = revenueData?.map((item) => {
    const itemUpdated = { ...item };
    itemUpdated.amount = setCurrencyFormat(itemUpdated.amount, true); return itemUpdated;
  });

  const totalAmount =
    revenueData && revenueData.length > 0 ? reduceArray(revenueData.map(x => x.amount)) : 0;
  const totalAmounts = (totalAmount / 1000000).toFixed(2);

  const sortedRevenueData = revenueData?.sort((a, b) => a?.salesYear > b?.salesYear ? 1 : -1) || [];

  const exportToExcel = () => {
    try {
      // Show loading notification
      Notification.newInstance({}, (notification) => {
        notification.notice({
          content: 'Preparing Excel file...',
          duration: 3,
          style: { top: 120, fontWeight: "bold" },
          maxCount: 10
        });
      });

      // Prepare data for export
      const exportData = revenueDataFormated.map(item => ({
        "Sales Year": item.salesYear || '',
        "Sales Month": item.salesMonthStr || '',
        "Sales Channel": item.salesChannel || '',
        "Amount": item.amount || '',
        "Order Count": item.orderCount || ''
      }));

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Set column widths for better formatting
      const columnWidths = [
        { wch: 12 }, // DATE
        { wch: 15 }, // BUNDLE_ASIN
        { wch: 50 }, // TITLE
        { wch: 15 }, // BUNDLES_SOLD
        { wch: 15 }, // TOTAL_SALES
      ];
      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Virtual Bundle Sales');

      // Generate filename with current date
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `${vendorNameMapping[vendor]} Sales Revenue Report.xlsx`;

      // Save the file
      XLSX.writeFile(workbook, filename);

      // Show success notification
      Notification.newInstance({}, (notification) => {
        notification.notice({
          content: 'Excel file downloaded successfully!',
          duration: 3,
          style: { top: 120, fontWeight: "bold", color: 'green' },
          maxCount: 10
        });
      });

    } catch (error) {
      console.error('Error exporting to Excel:', error);
      Notification.newInstance({}, (notification) => {
        notification.notice({
          content: 'Error exporting to Excel. Please try again.',
          duration: 5,
          style: { top: 120, fontWeight: "bold", color: 'red' },
          maxCount: 10
        });
      });
    }
  };


  return (
    <Container className="dashboard">
      <Card className="pb-3">
        <CardBody className="card-shadow p-3">
          <Row>
            <Col sm='10'>
              <FilterHeader
                filter={revenueDataComplete && revenueDataComplete.modal && revenueDataComplete.modal.filters}
                callback={changeFilter}
                headerTitle={`${vendorNameMapping[vendor]} Sales Revenue`}
              />
            </Col>
            <Col sm='2' className='text-right'>
              <button className="btn btn-sm btn-primary
                                             apply-date-button" style={{ marginTop: 60, marginRight: 10 }} type="button"
                title='Export to pdf' disabled={isLoading}
                onClick={exportToPDF}>Export PDF</button>
            </Col>

          </Row></CardBody>
      </Card>
      <div id="divToPrint" ref={container}>
        <Row>
          <MonthlySales setModel={openModel} revenueData={sortedRevenueData}
            title="Monthly Sales" isLoading={isLoading} />
        </Row>        
        <Row>
          {revenueData && revenueData.length > 0 ? (
            <YearBars setModel={openModel} revenueData={revenueData} title="Yearly Sales" isLoading={isLoading} />
          ) : (
            <Col md={12} lg={12} xl={12}>
              <Card>
                <CardBody className="card-shadow">
                  <div className="card__title">
                    <h5 className="bold-text">Yearly Sales</h5>
                  </div>
                  <div className="text-center p-4">
                    <p>No revenue data available for {vendor}</p>
                    {isLoading && <SmallLoader loading={isLoading} />}
                  </div>
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>
        <Row>
          {revenueData && revenueData.length > 0 ? (
            <HorizontalBars setModel={openModel} revenueData={revenueData} title="Orders" isLoading={isLoading} />
          ) : (
            <Col md={12} lg={12} xl={12}>
              <Card>
                <CardBody className="card-shadow">
                  <div className="card__title">
                    <h5 className="bold-text">Orders</h5>
                  </div>
                  <div className="text-center p-4">
                    <p>No order data available for {vendor}</p>
                    {isLoading && <SmallLoader loading={isLoading} />}
                  </div>
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>
        <Row>
          <Col md={12} lg={12} xl={12}>
            <Card>
              <CardBody className="card-shadow">
                {isLoading ? (<>
                  <div className="card__title">
                    <h5 className="bold-text">{t('Seasonality')}</h5>
                  </div>
                  <div
                    style={{ minHeight: '200px' }}
                    className="row align-items-center justify-content-center"
                  >
                    <SmallLoader loading={isLoading} />
                  </div>
                </>
                ) : (
                  <>
                    <div className="card__title">
                      <h5 className="bold-text">{t('Seasonality')}</h5>
                      <button id='btnBTCViewReport'
                        type="button"
                        className="view-report btn btn-primary btn-sm"
                        onClick={() => setModel(true)}
                      >
                        View Report
                      </button>
                    </div>
                    <div className="second-header-for-chart">
                      <div className="left">
                        <h5 className="bold-text colored">$ {totalAmounts} Million</h5>
                        <h5 className="bold-text">{t('Total Amount')}</h5>
                      </div>
                      <div className="right">
                        {/* <h5 className="bold-text colored">20%</h5>
                  <h5 className="bold-text">{t('Since last year')}</h5> */}
                      </div>
                    </div>
                    <table className="graph_table">
                      <tr className="graph_table_row">
                        <th className="graph_table_row_th_1">Year</th>
                        <th className="graph_table_row_th"> Q1</th>
                        <th className="graph_table_row_th"> Q2</th>
                        <th className="graph_table_row_th"> Q3</th>
                        <th className="graph_table_row_th"> Q4</th>
                      </tr>
                      {renderBTCData()}
                      <tr className="graph_table_row last">
                        <td className="graph_table_row_td"> </td>
                        <td className="graph_table_row_td">
                          <span className="left"> Jan</span>
                          <span className="center">Feb</span>
                          <span className="right">Mar</span>
                        </td>
                        <td className="graph_table_row_td">
                          <span className="left">Apr</span>
                          <span className="center">May</span>
                          <span className="right">Jun</span>
                        </td>
                        <td className="graph_table_row_td">
                          <span className="left">Jul</span>
                          <span className="center">Aug</span>
                          <span className="right">Sep</span>
                        </td>
                        <td className="graph_table_row_td">
                          <span className="left">Oct</span>
                          <span className="center">Nov</span>
                          <span className="right">Dec</span>
                        </td>
                      </tr>
                    </table>
                  </>)}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      <Modal
        isOpen={isModel}
        toggle={() => setModel(true)}
        style={{ maxWidth: '800px' }}
        className="modal-dialog ltr-support"
      >
        <div className="modal__header">
          <button
            className="lnr lnr-cross modal__close-btn"
            aria-label="modal__close-btn"
            type="button"
            onClick={() => setModel(false)}
          />
          <h4 className="text-modal  modal__title">{`${vendorNameMapping[vendor]} Sales Revenue Report`}</h4>
          <div className="ExcelTable">
            <div
              className="option table-action btn btn-sm"             
              onClick={exportToExcel}
            >
              Download
            </div>

          </div>
        </div>
        <div className="modal__body">
          <Row>
            <ResizableDataTable reactTableData={reactTableData} data={revenueDataFormated} />
          </Row>
        </div>
      </Modal>
    </Container>
  );

};

Revenue.propTypes = {
  dispatch: PropTypes.func.isRequired,
  rtl: RTLProps.isRequired,
  vendor: PropTypes.number.isRequired
};


export default connect(state => ({
  rtl: state.rtl,
}))(Revenue);
